import * as React from 'react';
import { AppUser } from '../../../../../../App';
import { Customer } from '../../../../../../../../models/customers';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { Button } from '../../../../../../../../components/Button/Button';
import * as Yup from 'yup';
import './CustomerStudentsForm.scss';
import { Autocomplete } from '../../../../../../../../components/Autocomplete/Autocomplete';
import {
  getForenameAndSurname,
  getFormName,
  searchFunctionForms,
  searchFunctionSchoolStudent,
  searchFunctionStudents,
  searchFunctionSchoolsByCode,
  getSchoolNameAndCode,
} from '../../../../../../../../helpers/autocomplete';
import { Student } from '../../../../../../../../models/students';
import * as propz from 'propz';
import {
  REASON_FOR_CANCELLATION_SERVER_TO_CLIENT_MAPPING,
  REASON_FOR_CANCELLATION,
  STUDENT_REQUEST_STATUS,
} from '../../../../../../../../consts/customer';
import { getYesOrNoForBoolean } from '../../../../../../../../helpers/select';
import { getCustomerName } from '../../../../../../../../helpers/accessor';
import * as Moment from 'moment';
import { BIRTH_DATE_FORMAT } from '../../../../../../../../consts/consts';
import {
  updateCustomerStudent,
  notifyImagesPending,
} from '../../../../../../../../services/superadmin/customerStudents';

interface Props {
  onCancel: () => void;
  onSubmit: (data: any) => void;
  user: AppUser;
  customer: Customer;
  allCustomerStudents: Student[];
  onDeleteStudentClick: (id: string) => void;
  onDeleteStudentNoNotificationClick: (id: string) => void;
  updateRequestStatusToNewCustomerStudentClick: (id: string) => void;
  updateRequestStatusNoMatchCustomerStudentClick: (id: string) => void;
}

export function CustomerStudentsForm(props: Props) {
  const {
    customer,
    onSubmit,
    onCancel,
    allCustomerStudents,
    onDeleteStudentClick,
    onDeleteStudentNoNotificationClick,
    updateRequestStatusToNewCustomerStudentClick,
    updateRequestStatusNoMatchCustomerStudentClick,
    user,
  } = props;

  const studentsForm = {
    students: customer.students.map(student => ({
      studentId: '',
      forename: '',
      surname: '',
      formDisabled: true,
      isApproved: false,
      commentEnabled: student.requestStatus === STUDENT_REQUEST_STATUS.POSTPONED,
      comment: propz.get(student, ['comment'], ''),
      schoolId: student.school ? student.school.schoolId.toString() : '',
      imagesPendingNotified: !!student.imagesPendingNotified,
    })),
    forms: customer.students.map(student => ({
      formId: undefined,
      form: {
        id: '',
        name: '',
      },
    })),
  };

  const StudentsSchema = Yup.object().shape({
    students: Yup.array().of(
      Yup.object().shape({
        schoolId: Yup.string().when('isApproved', {
          is: true,
          then: Yup.string().required('School is required'),
          otherwise: Yup.string(),
        }),
        studentId: Yup.string().when('isApproved', {
          is: true,
          then: Yup.string().required('Student is required'),
          otherwise: Yup.string(),
        }),
      })
    ),
    forms: Yup.array().of(
      Yup.object().shape({
        formId: Yup.string().when('isApproved', {
          is: true,
          then: Yup.string().required('Form is required'),
          otherwise: Yup.string(),
        }),
      })
    ),
  });

  const getSchools = (text: string) => {
    return searchFunctionSchoolsByCode(user, text);
  };

  const getStudents = (text, schoolId, setFieldValue, values, index) => {
    if (!schoolId) {
      return [];
    }

    const formId = values.forms[index]?.formId;

    if (!text) {
      return formId
        ? searchFunctionStudents(user, '', allCustomerStudents, schoolId, formId, values.students)
        : searchFunctionSchoolStudent(user, schoolId, allCustomerStudents, values.students, '');
    }

    if (formId) {
      return searchFunctionStudents(user, text, allCustomerStudents, schoolId, formId, values.students);
    } else {
      return searchFunctionSchoolStudent(user, schoolId, allCustomerStudents, values.students, text);
    }
  };

  const getForms = (text, schoolId, setFieldValue, values, index) => {
    const studentId = values.students[index].studentId;

    if (!studentId) {
      const forms = searchFunctionForms(user, schoolId, text);
      return forms;
    } else {
      return [
        {
          name: values.forms[index].form.name,
          id: values.forms[index].form.id,
        },
      ];
    }
  };

  const renderAlreadyLinkedStudents = () => {
    return allCustomerStudents
      .filter(student => {
        const studentId = propz.get(student, ['student', 'studentId']);
        return typeof studentId !== 'undefined';
      })
      .map((student, index) => {
        const forename = propz.get(student, ['student', 'forename'], '');
        const surname = propz.get(student, ['student', 'surname'], '');
        const schoolName = propz.get(student, ['school', 'schoolName'], '');
        const parentCode = propz.get(student, ['school', 'parentCode'], '');
        const schoolData = parentCode !== '' ? `${parentCode} ${schoolName}` : schoolName;

        return (
          <div key={`${student.id}`}>
            <div>
              {index + 1}. {forename} {surname}, School: {schoolData}
            </div>
          </div>
        );
      });
  };

  const customerName = getCustomerName(customer);

  return (
    <div>
      <div className="mb-4">
        <strong>Customer: </strong>
        {customerName}
      </div>
      <div className="mb-2">
        <strong>The students this customer is already linked to:</strong>
        {renderAlreadyLinkedStudents()}
      </div>
      <div>
        <strong>The students this customer has requested to be linked to:</strong>
      </div>
      <Formik
        initialValues={studentsForm}
        validationSchema={StudentsSchema}
        onSubmit={values => {
          onSubmit({
            students: values.students,
            forms: values.forms,
          });
        }}
      >
        {({ touched, errors, handleSubmit, values, setFieldValue, setErrors }) => (
          <Form>
            <div className="form-group custom-form-group">
              {customer.students.map((student, index) => {
                const {
                  forename,
                  surname,
                  school,
                  requestStatus,
                  form,
                  id,
                  isConfirmLegallyEntitled,
                  isLegalCarer,
                  isParentOrGuardian,
                  relation,
                  requestComment,
                  dayOfBorn,
                  birthday,
                  reasonForCancellation,
                } = student;
                const formikSchoolId = values.students[index].schoolId;

                const isNewRequestStatus = requestStatus === STUDENT_REQUEST_STATUS.NEW;
                const isNoMatchRequestStatus = requestStatus === STUDENT_REQUEST_STATUS.NO_MATCH;
                const isConfirmLegallyEntitledExist = typeof isConfirmLegallyEntitled !== 'undefined';
                const isDayOfBornExist = typeof dayOfBorn !== 'undefined';
                const isBirthdayExist = typeof birthday !== 'undefined';
                const studentBirthday = isBirthdayExist ? Moment(new Date(birthday)).format(BIRTH_DATE_FORMAT) : '';
                const isReasonForCancellationExist = typeof reasonForCancellation !== 'undefined';
                const isAutoAcceptUnavailable =
                  reasonForCancellation === REASON_FOR_CANCELLATION.AUTO_ACCEPT_UNAVAILABLE;
                const customerReasonForCancellation =
                  REASON_FOR_CANCELLATION_SERVER_TO_CLIENT_MAPPING[reasonForCancellation];

                const selectedSchool = propz.get(student, ['selectedSchool'], null);
                const selectedSchoolName = selectedSchool?.schoolName || '';
                const selectedSchoolLeaNumber = selectedSchool?.leaNumber || '';
                const selectedSchoolDfesNumber = selectedSchool?.dfesNumber || '';

                return (
                  <div key={id} className="student-block mb-4 p-3 border rounded">
                    <div className="student-details">
                      <div className="student-detail-row">
                        <div className="student-detail-label">
                          <strong>Forename:</strong>
                        </div>
                        <div className="student-detail-value">{forename}</div>
                      </div>
                      <div className="student-detail-row">
                        <div className="student-detail-label">
                          <strong>Surname:</strong>
                        </div>
                        <div className="student-detail-value">{surname}</div>
                      </div>
                      {isReasonForCancellationExist && (
                        <div className="student-detail-row">
                          <div className="student-detail-label">
                            <strong>No auto-accept reason:</strong>
                          </div>
                          <div className="student-detail-value">
                            <span className={isAutoAcceptUnavailable ? 'mReasonAutoAcceptUnavailable' : ''}>
                              {customerReasonForCancellation}
                            </span>
                          </div>
                        </div>
                      )}
                      {isDayOfBornExist && isBirthdayExist && (
                        <div className="student-detail-row">
                          <div className="student-detail-label">
                            <strong>Student's DOB:</strong>
                          </div>
                          <div className="student-detail-value">
                            {studentBirthday} ({dayOfBorn})
                          </div>
                        </div>
                      )}
                      {isConfirmLegallyEntitledExist && (
                        <>
                          <div className="student-detail-row">
                            <div className="student-detail-label">
                              <strong>Parent / Guardian:</strong>
                            </div>
                            <div className="student-detail-value">{getYesOrNoForBoolean(isParentOrGuardian)}</div>
                          </div>
                          <div className="student-detail-row">
                            <div className="student-detail-label">
                              <strong>Relation - Please state:</strong>
                            </div>
                            <div className="student-detail-value">{relation}</div>
                          </div>
                          <div className="student-detail-row">
                            <div className="student-detail-label">
                              <strong>Legal carer:</strong>
                            </div>
                            <div className="student-detail-value">{getYesOrNoForBoolean(isLegalCarer)}</div>
                          </div>
                          <div className="student-detail-row">
                            <div className="student-detail-label">
                              <strong>Other - Please state:</strong>
                            </div>
                            <div className="student-detail-value">{requestComment}</div>
                          </div>
                        </>
                      )}
                      <div className="student-detail-row">
                        <div className="student-detail-label">
                          <strong>Status:</strong>
                        </div>
                        <div className="student-detail-value">
                          <span className={isNewRequestStatus ? 'mRequestStatusNew' : 'mRequestStatusPostponed'}>
                            {requestStatus}
                          </span>
                        </div>
                      </div>
                      <div className="student-detail-row">
                        <div className="student-detail-label">
                          <strong>Notified:</strong>
                        </div>
                        <div
                          className={`student-detail-value ${
                            values.students[index].imagesPendingNotified ? 'notified-yes' : 'notified-no'
                          }`}
                        >
                          {values.students[index].imagesPendingNotified ? 'Yes' : 'No'}
                        </div>
                      </div>
                      {student.manualSchoolName && (
                        <div className="student-detail-row">
                          <div className="student-detail-label">
                            <strong>Manual school name:</strong>
                          </div>
                          <div className="student-detail-value">{student.manualSchoolName}</div>
                        </div>
                      )}
                    </div>

                    <div className="action-icons-row d-flex justify-content-end flex-nowrap mt-3">
                      <div className="bStudentLinkedIconBlock text-right">
                        <img
                          className="mr-2 mCursorPointer"
                          width="30px"
                          src="/dist/images/icon/mapsAndFlags.png"
                          title="Approve"
                          onClick={() => {
                            const students = [...values.students];
                            const currentStudent = values.students[index];
                            const formDisabled = currentStudent.formDisabled;

                            if (!formDisabled) {
                              const clearedStudent = {
                                ...values.students[index],
                                studentId: '',
                                forename: '',
                                surname: '',
                                formDisabled: true,
                                isApproved: false,
                              };
                              students.splice(index, 1, clearedStudent);
                            } else {
                              const updatedStudent = {
                                ...values.students[index],
                                formDisabled: false,
                                isApproved: true,
                              };
                              students.splice(index, 1, updatedStudent);
                            }

                            setFieldValue('students', students);
                          }}
                        />
                        {isNewRequestStatus ? (
                          <img
                            className="mr-2 mCursorPointer"
                            width="30px"
                            src="/dist/images/icon/clock.png"
                            title="Postpone"
                            onClick={() => {
                              const students = [...values.students];
                              const forms = [...values.forms];
                              const commentEnabled = values.students[index].commentEnabled;

                              const updatedStudent = {
                                ...values.students[index],
                                studentId: '',
                                forename: '',
                                surname: '',
                                formDisabled: true,
                                commentEnabled: !commentEnabled,
                              };
                              students.splice(index, 1, updatedStudent);
                              setFieldValue('students', students);

                              const emptyForm = {
                                formId: undefined,
                                form: {
                                  id: '',
                                  name: '',
                                },
                              };
                              forms.splice(index, 1, emptyForm);
                              setFieldValue('forms', forms);
                            }}
                          />
                        ) : (
                          <img
                            className="mr-2 mCursorPointer"
                            width="30px"
                            src="/dist/images/icon/reverseArrow.png"
                            title="Back to New"
                            onClick={() => updateRequestStatusToNewCustomerStudentClick(id)}
                          />
                        )}
                        {!isNoMatchRequestStatus && (
                          <img
                            className="mr-2 mCursorPointer"
                            width="30px"
                            src="/dist/images/icon/information.png"
                            title="No match"
                            onClick={() => updateRequestStatusNoMatchCustomerStudentClick(id)}
                          />
                        )}
                        <img
                          className="mr-2 mCursorPointer"
                          width="30px"
                          src="/dist/images/icon/multiply.png"
                          title="Decline"
                          onClick={() => onDeleteStudentClick(id)}
                        />
                        <img
                          className="mr-2 mCursorPointer"
                          width="30px"
                          src="/dist/images/icon/dustbin.png"
                          title="Delete"
                          onClick={() => onDeleteStudentNoNotificationClick(id)}
                        />
                        {!values.students[index].imagesPendingNotified && (
                          <img
                            className="mr-2 mCursorPointer"
                            width="30px"
                            src="/dist/images/icon/mail.png"
                            title="Send images pending notice"
                            onClick={async () => {
                              try {
                                await notifyImagesPending(user, customer.id, id);
                                const next = [...values.students];
                                next[index] = {
                                  ...next[index],
                                  imagesPendingNotified: true,
                                };
                                setFieldValue('students', next);
                              } catch (err) {
                                console.error('Could not send images-pending notice', err);
                              }
                            }}
                          />
                        )}
                      </div>
                    </div>

                    {values.students[index].commentEnabled && (
                      <div className="form-group">
                        <label>Reason for postponing:</label>
                        <textarea
                          rows={4}
                          onChange={event => {
                            const commentText = event.target.value;
                            const students = [...values.students];
                            const currentStudent = { ...students[index] };
                            currentStudent.comment = commentText;
                            students.splice(index, 1, currentStudent);
                            setFieldValue('students', students);
                          }}
                          value={values.students[index].comment}
                          className="form-control mb-3"
                        />
                      </div>
                    )}

                    {selectedSchool && (
                      <div className="selected-school-block mt-3 p-3 border rounded bg-light">
                        <h5>Selected School</h5>
                        <div>
                          <strong>School Name:</strong> {selectedSchool.schoolName}
                        </div>
                        <div>
                          <strong>LEA Number:</strong> {selectedSchool.leaNumber}
                        </div>
                        <div>
                          <strong>DFES Number:</strong> {selectedSchool.dfesNumber}
                        </div>
                      </div>
                    )}

                    {school && (
                      <div className="matched-school-block mt-3 p-3 border rounded bg-light">
                        <h5>Matched School</h5>
                        <div>
                          <strong>School Name:</strong> {school.schoolName || 'N/A'}
                        </div>
                        <div>
                          <strong>LEA Number:</strong> {school.leaNumber || 'N/A'}
                        </div>
                        <div>
                          <strong>DFES Number:</strong> {school.dfesNumber || 'N/A'}
                        </div>
                      </div>
                    )}

                    <div className="form-group mt-3">
                      <label>
                        <strong>School: </strong>
                      </label>
                      <Field name={`students.${index}.schoolId`}>
                        {() => (
                          <Autocomplete
                            searchFunction={getSchools}
                            getElementTitle={getSchoolNameAndCode}
                            customClass="mFullWidth mb-3"
                            disabled={values.students[index].formDisabled}
                            defaultItem={
                              student.school
                                ? {
                                    name: student.school.schoolName,
                                    code: student.school.schoolCode,
                                    schoolId: student.school.schoolId,
                                  }
                                : { name: '', code: '', schoolId: '' }
                            }
                            onSelect={async schoolEntry => {
                              if (!schoolEntry) {
                                const clearedStudents = values.students.map((s, i) =>
                                  i === index
                                    ? {
                                        ...s,
                                        schoolId: '',
                                        formDisabled: true,
                                        studentId: '',
                                        forename: '',
                                        surname: '',
                                      }
                                    : s
                                );
                                const clearedForms = values.forms.map((f, i) =>
                                  i === index ? { formId: undefined, form: { id: '', name: '' } } : f
                                );
                                setFieldValue('students', clearedStudents);
                                setFieldValue('forms', clearedForms);
                                return;
                              }

                              const newSchoolId = schoolEntry.schoolId?.toString() || schoolEntry.id.toString();

                              const nextStudents = values.students.map((s, i) =>
                                i === index
                                  ? {
                                      ...s,
                                      schoolId: newSchoolId,
                                      formDisabled: false,
                                      studentId: '',
                                      forename: '',
                                      surname: '',
                                    }
                                  : s
                              );

                              const nextForms = values.forms.map((f, i) =>
                                i === index ? { formId: undefined, form: { id: '', name: '' } } : f
                              );

                              setFieldValue(`students.${index}.schoolId`, newSchoolId);
                              setFieldValue(`students.${index}.formDisabled`, false);
                              setFieldValue(`forms.${index}.formId`, undefined);
                              setFieldValue(`students.${index}.studentId`, '');
                              setFieldValue(`students.${index}.forename`, '');
                              setFieldValue(`students.${index}.surname`, '');

                              try {
                                const updated = await updateCustomerStudent(user, customer.id, id, {
                                  schoolId: newSchoolId,
                                });
                                if (updated.school) {
                                  setFieldValue(`students.${index}.schoolId`, updated.school.schoolId.toString());
                                }
                              } catch (err) {
                                console.error(err);
                              }
                            }}
                          />
                        )}
                      </Field>
                      <ErrorMessage
                        component="div"
                        className="alert alert-danger"
                        name={`students.${index}.schoolId`}
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        <strong>Form: </strong>
                      </label>
                      <Field name={`forms.${index}.formId`}>
                        {() => (
                          <Autocomplete
                            key={`form-autocomplete-${index}-${formikSchoolId}`}
                            searchFunction={text => {
                              return getForms(text, formikSchoolId, setFieldValue, values, index);
                            }}
                            getElementTitle={getFormName}
                            disabled={!formikSchoolId || values.students[index].formDisabled}
                            customClass="mFullWidth mb-3"
                            defaultItem={values.forms[index]}
                            onSelect={form => {
                              const forms = [...values.forms].slice();
                              forms[index].formId = form.id;
                              forms[index].form.name = form.name;
                              forms[index].form.id = form.id;
                              setFieldValue('forms', forms);
                            }}
                          />
                        )}
                      </Field>
                      <ErrorMessage component="div" className="alert alert-danger" name={`forms.${index}.formId`} />
                    </div>

                    <div className="form-group">
                      <label>
                        <strong>Student: </strong>
                      </label>
                      <Field name={`students.${index}.studentId`}>
                        {() => (
                          <Autocomplete
                            key={`student-autocomplete-${index}-${formikSchoolId}`}
                            searchFunction={text => {
                              return getStudents(text, formikSchoolId, setFieldValue, values, index);
                            }}
                            getElementTitle={getForenameAndSurname}
                            customClass="mFullWidth mb-3"
                            defaultItem={values.students[index]}
                            disabled={!formikSchoolId || values.students[index].formDisabled}
                            onSelect={student => {
                              const students = [...values.students];
                              students[index].studentId = student.id;
                              students[index].forename = student.forename;
                              students[index].surname = student.surname;
                              setFieldValue('students', students);
                              const isFormIdExist =
                                typeof values.forms[index].formId !== 'undefined' && values.forms[index].formId !== '';
                              if (!isFormIdExist) {
                                const forms = [...values.forms];
                                forms[index].formId = student.form.formId;
                                forms[index].form.name = student.form.formName;
                                forms[index].form.id = student.form.formId;
                                setFieldValue('forms', forms);
                              }
                            }}
                          />
                        )}
                      </Field>
                      <ErrorMessage
                        component="div"
                        className="alert alert-danger"
                        name={`students.${index}.studentId`}
                      />
                    </div>
                  </div>
                );
              })}
              <div className="d-flex justify-content-end">
                <Button
                  onClick={onCancel}
                  text={'Cancel'}
                  customClass={'btn btn-primary mt-3 mb-3 mr-3 btn-secondary'}
                />
                <Button
                  onClick={handleSubmit}
                  text={'Save'}
                  customClass={'mt-3 mb-3 btn btn-primary'}
                  type={'submit'}
                />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}
